import { useEffect, useState, useRef } from 'react'
import type { SSEStatus } from '../../hooks/useSSE'

interface SSEStatusIndicatorProps {
  status: SSEStatus
}

// ─────────────────────────────────────────────────────────────
// SSEStatusIndicator
//
// Only shows a banner when the connection DROPS after the initial
// connect. The first 'connected' event is silent — we don't want
// to show "Live updates restored" every time the page loads.
//
// State machine:
//   initial (never connected)
//     → connected: silent, mark hasEverConnected = true
//     → disconnected/reconnecting: show yellow banner
//   hasEverConnected
//     → disconnected/reconnecting: show yellow banner
//     → connected: show green "restored" for 2s then hide
// ─────────────────────────────────────────────────────────────
export default function SSEStatusIndicator({ status }: SSEStatusIndicatorProps) {
  const [visible, setVisible] = useState(false)
  const hasEverConnected = useRef(false)
  const wasVisiblyDisconnected = useRef(false)  // true only if the yellow banner was shown
  const dismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (status === 'connected') {
      if (!hasEverConnected.current) {
        // First connection on page load — stay silent
        hasEverConnected.current = true
        return
      }
      // Only show "restored" if the user actually SAW a disconnection banner
      if (wasVisiblyDisconnected.current) {
        wasVisiblyDisconnected.current = false
        setVisible(true)
        if (dismissTimer.current) clearTimeout(dismissTimer.current)
        dismissTimer.current = setTimeout(() => setVisible(false), 2000)
      } else {
        // Silent reconnect (token rotation, tab re-focus that resolved quickly)
        setVisible(false)
      }

    } else if (status === 'disconnected' || status === 'reconnecting') {
      // Only show the yellow banner if we've connected at least once
      if (hasEverConnected.current) {
        wasVisiblyDisconnected.current = true
        if (dismissTimer.current) clearTimeout(dismissTimer.current)
        setVisible(true)
      }
    }

    return () => {
      if (dismissTimer.current) clearTimeout(dismissTimer.current)
    }
  }, [status])

  if (!visible) return null

  const isReconnecting = status === 'reconnecting'
  const isDisconnected = status === 'disconnected'
  const isBackOnline  = status === 'connected'

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.45rem 0.875rem',
      marginBottom: '0.75rem',
      borderRadius: '0',
      borderLeft: `3px solid ${isBackOnline ? 'var(--success)' : 'var(--warning)'}`,
      borderTop: `1px solid ${isBackOnline ? 'rgba(34,197,94,0.3)' : 'rgba(234,179,8,0.3)'}`,
      borderRight: `1px solid ${isBackOnline ? 'rgba(34,197,94,0.3)' : 'rgba(234,179,8,0.3)'}`,
      borderBottom: `1px solid ${isBackOnline ? 'rgba(34,197,94,0.3)' : 'rgba(234,179,8,0.3)'}`,
      background: isBackOnline ? 'rgba(34,197,94,0.08)' : 'rgba(234,179,8,0.08)',
      fontSize: '0.78rem',
      fontWeight: 500,
      color: isBackOnline ? 'var(--success)' : 'var(--warning)',
      animation: 'sseFadeIn 0.2s ease',
    }}>
      <style>{`@keyframes sseFadeIn { from { opacity: 0; transform: translateY(-4px) } to { opacity: 1; transform: translateY(0) } }`}</style>

      <span style={{
        width: '7px',
        height: '7px',
        borderRadius: '50%',
        flexShrink: 0,
        background: isBackOnline ? 'var(--success)' : 'var(--warning)',
        animation: isReconnecting ? 'pulseDot 1s infinite' : 'none',
      }} />

      {isDisconnected  && 'Live updates disconnected — attempting to reconnect...'}
      {isReconnecting  && 'Reconnecting...'}
      {isBackOnline    && '✓ Live updates restored'}
    </div>
  )
}
